import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from './api';
import type {
  ChatChannel,
  ChatChannelsResponse,
  ChatHistoryResponse,
  ChatMessage,
  ChatSocketEvent,
} from './types';

/**
 * The chat client.
 *
 * Mounted above the router so it survives navigation: the socket, the backlog
 * and the unread counts persist while the player walks between pages, which is
 * the whole point of a dock rather than a chat *page*.
 *
 * Division of labour matches the server. The socket only ever *delivers*;
 * everything that changes state — sending, deleting, reporting — is an ordinary
 * authenticated fetch. So a dropped socket degrades to "I stop seeing other
 * people" rather than "I cannot talk", and reconnecting re-fetches the backlog
 * to close the gap.
 */

const MAX_BUFFERED = 200;

interface ChatState {
  connected: boolean;
  channels: ChatChannel[];
  activeKey: string | null;
  messages: ChatMessage[];
  pinned: ChatMessage[];
  canModerate: boolean;
  loadingHistory: boolean;
  online: number;
  maxLength: number;
  /** Total unread across every channel — what the collapsed bubble shows. */
  totalUnread: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectChannel: (key: string) => void;
  send: (body: string, announcement?: boolean) => Promise<void>;
  removeMessage: (id: string) => Promise<void>;
  reportMessage: (id: string, reason: string) => Promise<void>;
  pinMessage: (id: string, pinned: boolean) => Promise<void>;
  blockUser: (userId: string, blocked: boolean) => Promise<void>;
  reload: () => void;
}

const ChatContext = createContext<ChatState | null>(null);

/** Remembers the dock's open/closed state and last room between visits. */
const readStored = (key: string, fallback: string) => {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};
const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode; the dock simply forgets between visits */
  }
};

export function ChatProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pinned, setPinned] = useState<ChatMessage[]>([]);
  const [canModerate, setCanModerate] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [online, setOnline] = useState(0);
  const [maxLength, setMaxLength] = useState(500);
  const [open, setOpenState] = useState(() => readStored('pg.chat.open', '0') === '1');

  const socketRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const retryTimer = useRef<number | null>(null);
  /** Read inside socket callbacks, which close over their first render. */
  const activeKeyRef = useRef<string | null>(null);
  const channelsRef = useRef<ChatChannel[]>([]);

  activeKeyRef.current = activeKey;
  channelsRef.current = channels;

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeStored('pg.chat.open', next ? '1' : '0');
  }, []);

  /* --- Channel list ------------------------------------------------------ */

  const loadChannels = useCallback(async () => {
    try {
      const result = await api.get<ChatChannelsResponse>('/chat/channels');
      setChannels(result.channels);
      setOnline(result.online);
      setMaxLength(result.limits.maxLength);

      setActiveKey((current) => {
        if (current && result.channels.some((channel) => channel.key === current)) return current;
        const remembered = readStored('pg.chat.channel', '');
        if (remembered && result.channels.some((channel) => channel.key === remembered)) {
          return remembered;
        }
        return result.channels[0]?.key ?? null;
      });
    } catch {
      /* an unreachable API is already surfaced by the connection dot */
    }
  }, []);

  /* --- History ----------------------------------------------------------- */

  const loadHistory = useCallback(async (key: string) => {
    setLoadingHistory(true);
    try {
      const result = await api.get<ChatHistoryResponse>(`/chat/channels/${key}/messages`);
      // Guard against a slow response for a channel the player has since left.
      if (activeKeyRef.current !== key) return;
      setMessages(result.messages);
      setPinned(result.pinned);
      setCanModerate(result.canModerate);
      // Opening a room reads it, so clear the badge without a round trip.
      setChannels((current) =>
        current.map((channel) => (channel.key === key ? { ...channel, unread: 0 } : channel)),
      );
    } catch {
      if (activeKeyRef.current === key) {
        setMessages([]);
        setPinned([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !activeKey) return;
    writeStored('pg.chat.channel', activeKey);
    void loadHistory(activeKey);
  }, [enabled, activeKey, loadHistory]);

  /* --- Socket ------------------------------------------------------------ */

  const handleEvent = useCallback((event: ChatSocketEvent) => {
    switch (event.type) {
      case 'ready':
        setOnline(event.online);
        return;

      case 'channels':
        // The socket's list is authoritative about *membership* (joining a
        // guild lights its tab up live); unread counts stay with the REST
        // response, which is the side that actually counts rows.
        setChannels((current) =>
          event.channels.map((channel) => ({
            ...channel,
            hidden: current.find((row) => row.key === channel.key)?.hidden ?? false,
            unread: current.find((row) => row.key === channel.key)?.unread ?? 0,
          })),
        );
        return;

      case 'message': {
        const isActive = channelsRef.current.some(
          (channel) => channel.id === event.channelId && channel.key === activeKeyRef.current,
        );

        if (isActive) {
          setMessages((current) => {
            // The sender already appended their own copy from the POST reply;
            // the broadcast of that same message must not double it.
            if (current.some((message) => message.id === event.message.id)) return current;
            const next = [...current, event.message];
            return next.length > MAX_BUFFERED ? next.slice(-MAX_BUFFERED) : next;
          });
        } else {
          setChannels((current) =>
            current.map((channel) =>
              channel.id === event.channelId
                ? { ...channel, unread: Math.min(99, channel.unread + 1) }
                : channel,
            ),
          );
        }
        return;
      }

      case 'updated':
      case 'deleted':
        setMessages((current) =>
          current.map((message) => (message.id === event.message.id ? event.message : message)),
        );
        setPinned((current) => {
          const without = current.filter((message) => message.id !== event.message.id);
          return event.message.isPinned && !event.message.deleted
            ? [event.message, ...without]
            : without;
        });
        return;

      case 'presence':
        setOnline(event.online);
        return;

      default:
        return;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;

    const connect = () => {
      if (disposed) return;

      const url = new URL('/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

      let socket: WebSocket;
      try {
        socket = new WebSocket(url.toString());
      } catch {
        scheduleRetry();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        setConnected(true);
        retryRef.current = 0;
        // Re-fetch on every (re)connect, not just the first: anything said
        // while the socket was down is missing from the buffer, and a chat
        // with a silent hole in it is worse than one that briefly reloads.
        void loadChannels();
        const key = activeKeyRef.current;
        if (key) void loadHistory(key);
      };

      socket.onmessage = (event) => {
        if (disposed) return;
        try {
          handleEvent(JSON.parse(event.data as string) as ChatSocketEvent);
        } catch {
          /* a frame we cannot parse is a frame we ignore */
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        setConnected(false);
        scheduleRetry();
      };

      socket.onerror = () => socket.close();
    };

    // Exponential backoff, capped: a server that is down should not be hit by
    // every open tab once a second, but a brief blip should recover fast.
    const scheduleRetry = () => {
      if (disposed || retryTimer.current !== null) return;
      const delay = Math.min(1000 * 2 ** retryRef.current, 30_000);
      retryRef.current += 1;
      retryTimer.current = window.setTimeout(() => {
        retryTimer.current = null;
        connect();
      }, delay);
    };

    void loadChannels();
    connect();

    return () => {
      disposed = true;
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, handleEvent, loadChannels, loadHistory]);

  /* --- Actions ----------------------------------------------------------- */

  const selectChannel = useCallback((key: string) => setActiveKey(key), []);

  const send = useCallback(
    async (body: string, announcement = false) => {
      const key = activeKeyRef.current;
      if (!key) return;

      const result = await api.post<{ message: ChatMessage }>(
        `/chat/channels/${key}/messages`,
        { body, ...(announcement ? { announcement: true } : {}) },
      );

      // Appended straight away rather than waiting for the broadcast, so the
      // input feels instant. The socket copy is de-duplicated by id.
      setMessages((current) =>
        current.some((message) => message.id === result.message.id)
          ? current
          : [...current, result.message].slice(-MAX_BUFFERED),
      );
    },
    [],
  );

  const removeMessage = useCallback(async (id: string) => {
    await api.post(`/chat/messages/${id}/remove`, {});
  }, []);

  const reportMessage = useCallback(async (id: string, reason: string) => {
    await api.post(`/chat/messages/${id}/report`, { reason });
  }, []);

  const pinMessage = useCallback(async (id: string, isPinned: boolean) => {
    await api.post(`/chat/messages/${id}/pin`, { pinned: isPinned });
  }, []);

  const blockUser = useCallback(
    async (userId: string, blocked: boolean) => {
      await api.post('/chat/block', { userId, blocked });
      // A block changes how past messages render, so the room is re-read
      // rather than patched — the server decides what a blocked line looks
      // like, and re-fetching is how the client learns it.
      const key = activeKeyRef.current;
      if (key) await loadHistory(key);
    },
    [loadHistory],
  );

  const reload = useCallback(() => {
    void loadChannels();
    const key = activeKeyRef.current;
    if (key) void loadHistory(key);
  }, [loadChannels, loadHistory]);

  const totalUnread = useMemo(
    () => channels.reduce((sum, channel) => sum + (channel.hidden ? 0 : channel.unread), 0),
    [channels],
  );

  const value = useMemo<ChatState>(
    () => ({
      connected,
      channels,
      activeKey,
      messages,
      pinned,
      canModerate,
      loadingHistory,
      online,
      maxLength,
      totalUnread,
      open,
      setOpen,
      selectChannel,
      send,
      removeMessage,
      reportMessage,
      pinMessage,
      blockUser,
      reload,
    }),
    [
      connected, channels, activeKey, messages, pinned, canModerate, loadingHistory,
      online, maxLength, totalUnread, open, setOpen, selectChannel, send,
      removeMessage, reportMessage, pinMessage, blockUser, reload,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used inside a ChatProvider');
  return context;
}

/** Turns an ApiError into something worth showing above the input box. */
export const chatErrorMessage = (error: unknown) =>
  error instanceof ApiError ? error.message : 'Could not send that message.';
